import { ref, computed, h } from "vue";
import {
	createColumnHelper,
	getCoreRowModel,
	getSortedRowModel,
	getFilteredRowModel,
	getExpandedRowModel,
	getPaginationRowModel,
	useVueTable,
} from "@tanstack/vue-table";
import dayjs from "dayjs";

const columnHelper = createColumnHelper();

export function useUserTable(usersGetter, { onRefresh, onEdit, isRefreshing }) {
	const sorting = ref([{ id: "username", desc: false }]);
	const globalFilter = ref("");
	const columnFilters = ref([]);
	const expanded = ref({});
	const pagination = ref({ pageIndex: 0, pageSize: 25 });

	const columns = [
		columnHelper.display({
			id: "expand",
			header: "",
			enableHiding: false,
			cell: ({ row }) =>
				h(
					"button",
					{
						class: "expand-toggle",
						type: "button",
						onClick: row.getToggleExpandedHandler(),
						"data-testid": "user-row-expand",
					},
					row.getIsExpanded()
						? h("i", { class: "fas fa-chevron-down" })
						: h("i", { class: "fas fa-chevron-right" })
				),
		}),
		columnHelper.display({
			id: "actions",
			header: "Aktionen",
			cell: ({ row }) => {
				const buttons = [];

				if (row.original.substituteUrl) {
					buttons.push(
						h(
							"a",
							{
								class: "btn btn-sm btn-outline-secondary me-1",
								title: "In Account wechseln",
								href: row.original.substituteUrl,
								"data-testid": "user-row-substitute",
								onClick: (event) => event.stopPropagation(),
							},
							h("i", { class: "fas fa-random" })
						)
					);
				}

				buttons.push(
					h(
						"button",
						{
							type: "button",
							class: "btn btn-sm btn-outline-secondary me-1",
							title: "Bearbeiten",
							"data-testid": "user-row-edit",
							onClick: (event) => {
								event.stopPropagation();
								onEdit(row.original);
							},
						},
						h("i", { class: "fas fa-pen" })
					)
				);

				if (row.original.isLdapAccount) {
					const refreshing = isRefreshing(row.original.id);
					buttons.push(
						h(
							"button",
							{
								type: "button",
								class: "btn btn-sm btn-outline-secondary",
								title: "Von LDAP aktualisieren",
								disabled: refreshing,
								"data-testid": "user-row-refresh",
								onClick: (event) => {
									event.stopPropagation();
									onRefresh(row.original.id);
								},
							},
							h("i", { class: refreshing ? "fas fa-spinner fa-spin" : "fas fa-sync-alt" })
						)
					);
				}

				return h("div", { class: "d-flex justify-content-center" }, buttons);
			},
		}),
		columnHelper.accessor((row) => (row.isLdapAccount ? "LDAP" : "Lokal"), {
			id: "type",
			header: "Typ",
			cell: ({ row }) =>
				h(
					"span",
					{ class: row.original.isLdapAccount ? "badge bg-primary" : "badge bg-secondary" },
					row.original.isLdapAccount ? "LDAP" : "Lokal"
				),
		}),
		columnHelper.accessor("username", {
			header: "Benutzername",
			cell: ({ row }) => row.original.account,
		}),
		columnHelper.accessor("displayName", {
			header: "Angezeigter Name",
		}),
		columnHelper.accessor("mail", {
			header: "E-Mail",
			cell: ({ getValue }) => getValue() || "-",
		}),
		columnHelper.accessor((row) => (row.roles || []).map((role) => role.title).join(", "), {
			id: "roles",
			header: "Rollen",
			cell: ({ getValue }) => getValue() || "-",
			filterFn: (row, columnId, filterValue) => {
				if (!filterValue) return true;
				return (row.original.roles || []).some((role) => String(role.id) === String(filterValue));
			},
		}),
		columnHelper.accessor("lastLogin", {
			header: "Letzter Login",
			cell: ({ getValue }) => {
				const value = getValue();
				return value ? dayjs(value).format("DD.MM.YYYY HH:mm") : "noch nie";
			},
		}),
	];

	const data = computed(() => {
		const users = typeof usersGetter === "function" ? usersGetter() : usersGetter;
		return users || [];
	});

	const table = useVueTable({
		get data() {
			return data.value;
		},
		columns,
		state: {
			get sorting() {
				return sorting.value;
			},
			get globalFilter() {
				return globalFilter.value;
			},
			get columnFilters() {
				return columnFilters.value;
			},
			get expanded() {
				return expanded.value;
			},
			get pagination() {
				return pagination.value;
			},
		},
		onSortingChange: (updaterOrValue) => {
			sorting.value = typeof updaterOrValue === "function" ? updaterOrValue(sorting.value) : updaterOrValue;
		},
		onGlobalFilterChange: (updaterOrValue) => {
			globalFilter.value =
				typeof updaterOrValue === "function" ? updaterOrValue(globalFilter.value) : updaterOrValue;
			pagination.value = { ...pagination.value, pageIndex: 0 };
		},
		onColumnFiltersChange: (updaterOrValue) => {
			columnFilters.value =
				typeof updaterOrValue === "function" ? updaterOrValue(columnFilters.value) : updaterOrValue;
			pagination.value = { ...pagination.value, pageIndex: 0 };
		},
		onExpandedChange: (updaterOrValue) => {
			expanded.value = typeof updaterOrValue === "function" ? updaterOrValue(expanded.value) : updaterOrValue;
		},
		onPaginationChange: (updaterOrValue) => {
			pagination.value = typeof updaterOrValue === "function" ? updaterOrValue(pagination.value) : updaterOrValue;
		},
		getRowCanExpand: () => true,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		globalFilterFn: "includesString",
	});

	return { table, sorting, globalFilter, columnFilters, expanded, pagination };
}
